import Phaser from 'phaser';
import {
  GAME_W,
  GAME_H,
  BUILD,
  PLATE,
  BUBBLE,
  CUSTOMER,
  BELL,
  COIN_BOARD,
  COIN_CARD,
  JAR,
} from '../config.ts';
import {
  DIFFICULTIES,
  stagesFor,
  ALL_FLAVOR_IDS,
  type Difficulty,
  type DifficultyId,
  type Stage,
  type Order,
} from '../levels.ts';
import { CUSTOMER_NAMES, SHOPKEEPER } from '../../assets/builders/animals.ts';
import { Audio } from '../audio.ts';
import { IceCream } from '../objects/IceCream.ts';
import { OrderBubble } from '../objects/OrderBubble.ts';
import { CoinJar } from '../objects/CoinJar.ts';
import { PaymentPanel } from '../objects/PaymentPanel.ts';

type Phase = 'idle' | 'build' | 'pay' | 'change';

/** The four toppings — always offered, so a kid can decorate freely. */
const TOPPINGS = ['top_cherry', 'top_sprinkles', 'top_wafer', 'top_whip'];

const randInt = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min;

/**
 * The whole game loop, with no reading required:
 *   a customer shows a picture order → child builds it (counting aloud) → rings
 *   the bell to serve → (pays / makes change) → reward → next customer.
 *
 * The bear is the fixed shopkeeper; six animals rotate as customers. Difficulty
 * (chosen in the menu) scales the numbers and how many flavors the shelf shows.
 */
export class PlayScene extends Phaser.Scene {
  private difficulty!: Difficulty;
  private stages!: Stage[];
  private stageIndex = 0;
  private roundsInStage = 0;
  private phase: Phase = 'idle';
  private order!: Order;
  private customerIdx = -1;

  private iceCream!: IceCream;
  private bubble!: OrderBubble;
  private jar!: CoinJar;
  private shopkeeper!: Phaser.GameObjects.Image;
  private shelf!: Phaser.GameObjects.Container;
  private bellBtn!: Phaser.GameObjects.Container;
  private undoZone!: Phaser.GameObjects.Zone;
  private portraits: Phaser.GameObjects.Image[] = [];
  private payment: PaymentPanel | null = null;

  constructor() {
    super('Play');
  }

  init(data: { difficulty?: DifficultyId }): void {
    this.difficulty = DIFFICULTIES[data?.difficulty ?? 'medium'] ?? DIFFICULTIES.medium;
    this.stages = stagesFor(this.difficulty);
    this.stageIndex = 0;
    this.roundsInStage = 0;
    this.customerIdx = -1;
  }

  create(): void {
    this.add.image(GAME_W / 2, GAME_H / 2, 'bg').setDisplaySize(GAME_W, GAME_H).setDepth(-10);

    // Persistent right-side shop fixtures (active only during payment).
    this.add.image(COIN_BOARD.x, COIN_BOARD.y, 'coin_board').setDisplaySize(300, 116).setDepth(2);
    this.add.image(COIN_CARD.x, COIN_CARD.y, 'coin_card').setDisplaySize(300, 162).setDepth(2);
    this.jar = new CoinJar(this, JAR.x, JAR.y);
    this.jar.setDepth(3);

    // Bear shopkeeper, fixed behind the counter.
    this.shopkeeper = this.add
      .image(CUSTOMER.x, CUSTOMER.y, `animal_${SHOPKEEPER}`)
      .setOrigin(0.5, 1)
      .setScale(CUSTOMER.scale)
      .setDepth(4);

    // Waiting customers, framed at the counter (front one = now serving).
    this.buildPortraits();

    // The plate + the ice cream being built.
    this.add.image(PLATE.x, PLATE.y, 'plate').setDisplaySize(240, 88).setDepth(5);
    this.iceCream = new IceCream(this, BUILD.x, BUILD.y);
    this.iceCream.setDepth(6);

    // Invisible tap zone over the stack: tap the ice cream to remove a scoop.
    this.undoZone = this.add
      .zone(BUILD.x, BUILD.y - 40, 180, 360)
      .setDepth(7)
      .setInteractive({ useHandCursor: true });
    this.undoZone.on('pointerdown', () => this.onUndo());

    this.bubble = new OrderBubble(this, BUBBLE.x, BUBBLE.y, 0.82);
    this.bubble.setDepth(8);

    this.shelf = this.add.container(0, 0).setDepth(20);
    this.makeBell();
    this.buildTopUI();

    this.input.once('pointerdown', () => Audio.unlock());

    this.exposeDebugApi();
    this.startRound();
  }

  private get stage(): Stage {
    return this.stages[Math.min(this.stageIndex, this.stages.length - 1)];
  }

  // ── Per-round flow ────────────────────────────────────────────────────────
  private startRound(): void {
    this.phase = 'build';
    this.order = this.stage.makeOrder(this.difficulty);

    this.iceCream.clearScoops();
    this.iceCream.setBase(this.order.base);

    this.customerIdx = (this.customerIdx + 1) % CUSTOMER_NAMES.length;
    this.updatePortraits();
    this.bouncePortrait(0);

    this.bubble.show(this.order);
    this.rebuildShelf();
    this.showShelf(true);
    this.bellBtn.setScale(1);

    this.time.delayedCall(650, () => {
      if (this.phase === 'build') Audio.speakNumber(this.order.total);
    });
  }

  /** Flavors offered on the shelf this round: the order's flavors + distractors. */
  private shelfFlavors(): string[] {
    const want = Array.from(new Set(this.order.scoops.map((s) => s.flavor)));
    const pool = ALL_FLAVOR_IDS.filter((f) => !want.includes(f));
    // Shuffle distractors deterministically-enough via splice.
    const out = [...want];
    while (out.length < this.difficulty.flavorChoices && pool.length) {
      out.push(pool.splice(randInt(0, pool.length - 1), 1)[0]);
    }
    // Keep a stable visual order so buttons don't jump around mid-build.
    return out.sort((a, b) => ALL_FLAVOR_IDS.indexOf(a) - ALL_FLAVOR_IDS.indexOf(b));
  }

  private onPickBase(base: 'cone' | 'cup'): void {
    if (this.phase !== 'build') return;
    Audio.unlock();
    Audio.pop();
    this.iceCream.setBase(base);
  }

  private onPlaceScoop(flavor: string): void {
    if (this.phase !== 'build') return;
    Audio.unlock();
    Audio.pop();
    const n = this.iceCream.addScoop(flavor);
    this.time.delayedCall(120, () => Audio.speakNumber(n));
    this.pulseServeHint();
  }

  private onPickTopping(key: string): void {
    if (this.phase !== 'build') return;
    Audio.unlock();
    Audio.pop();
    this.iceCream.setTopping(key);
  }

  private onUndo(): void {
    // Tapping the ice cream removes the last scoop (a forgiving "take it back").
    if (this.phase !== 'build' || this.iceCream.scoopCount === 0) return;
    Audio.unlock();
    this.iceCream.removeLast();
  }

  private onServe(): void {
    if (this.phase !== 'build') return;
    Audio.unlock();
    if (this.iceCream.scoopCount === 0) {
      this.iceCream.wobble();
      return;
    }
    if (this.iceCream.matches(this.order)) {
      this.onBuildSuccess();
    } else {
      this.iceCream.wobble();
      Audio.wobble();
      this.time.delayedCall(120, () => Audio.speak('Try again', { pitch: 1.2 }));
      this.showTryAgainIcon();
    }
  }

  private onBuildSuccess(): void {
    this.phase = 'idle';
    this.showShelf(false);
    this.bubble.hide();
    Audio.chimeHappy();
    Audio.speak('Yay!', { pitch: 1.35 });
    this.happyCustomer();
    this.popStar(this.iceCream.x, this.iceCream.y + this.iceCream.topOfStack());

    this.time.delayedCall(1100, () => {
      if (this.stage.doChange) this.startChange();
      else if (this.stage.doPay) this.startPay();
      else this.completeCustomer();
    });
  }

  // ── Payment phases ──────────────────────────────────────────────────────────
  private startPay(): void {
    this.phase = 'pay';
    Audio.speakNumber(this.order.price ?? 0);
    this.payment = new PaymentPanel(this, {
      mode: 'pay',
      target: this.order.price ?? 0,
      onComplete: () => this.completeCustomer(),
    });
  }

  private startChange(): void {
    this.phase = 'change';
    const change = (this.order.paidWith ?? 0) - (this.order.price ?? 0);
    this.payment = new PaymentPanel(this, {
      mode: 'change',
      target: change,
      paidWith: this.order.paidWith,
      onComplete: () => this.completeCustomer(),
    });
  }

  // ── Reward + advance ──────────────────────────────────────────────────────
  private completeCustomer(): void {
    this.phase = 'idle';
    if (this.payment) {
      this.payment.destroy();
      this.payment = null;
    }
    Audio.chimeHappy();
    Audio.speak('Thank you!', { pitch: 1.3 });
    this.happyCustomer();
    this.hopServedCustomer();

    const coins = 1 + (this.stage.doPay ? 1 : 0);
    for (let i = 0; i < coins; i++) this.jar.addCoin(i * 220);

    this.roundsInStage += 1;
    const advancing =
      this.roundsInStage >= this.difficulty.roundsPerStage && this.stageIndex < this.stages.length - 1;

    this.time.delayedCall(1500, () => {
      if (advancing) {
        this.stageIndex += 1;
        this.roundsInStage = 0;
        this.celebrateLevelUp(() => this.startRound());
      } else {
        this.startRound();
      }
    });
  }

  private celebrateLevelUp(done: () => void): void {
    Audio.fanfare();
    for (let i = 0; i < 6; i++) {
      const x = GAME_W * 0.3 + Math.random() * GAME_W * 0.4;
      this.time.delayedCall(i * 120, () => this.popStar(x, GAME_H * 0.3 + Math.random() * 120));
    }
    this.time.delayedCall(1600, done);
  }

  // ── Feedback visuals ──────────────────────────────────────────────────────
  private popStar(x: number, y: number): void {
    const star = this.add.image(x, y, 'star').setDisplaySize(110, 110).setDepth(70).setScale(0);
    this.tweens.add({ targets: star, scale: 1, duration: 320, ease: 'Back.Out' });
    this.tweens.add({
      targets: star,
      y: y - 140,
      alpha: 0,
      duration: 900,
      delay: 500,
      ease: 'Cubic.In',
      onComplete: () => star.destroy(),
    });
  }

  private showTryAgainIcon(): void {
    const icon = this.add
      .image(this.iceCream.x, this.iceCream.y + this.iceCream.topOfStack() - 30, 'try_again')
      .setDisplaySize(104, 104)
      .setDepth(70)
      .setScale(0);
    this.tweens.add({ targets: icon, scale: 1, duration: 260, ease: 'Back.Out' });
    this.tweens.add({
      targets: icon,
      alpha: 0,
      duration: 400,
      delay: 900,
      onComplete: () => icon.destroy(),
    });
  }

  private happyCustomer(): void {
    this.tweens.add({
      targets: this.shopkeeper,
      y: CUSTOMER.y - 30,
      duration: 200,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.InOut',
    });
  }

  /** The served (front) customer gives a happy hop. */
  private hopServedCustomer(): void {
    const head = this.portraits[0];
    if (!head) return;
    const baseY = head.getData('baseY') as number;
    this.tweens.add({
      targets: head,
      y: baseY - 18,
      duration: 180,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.InOut',
      onComplete: () => head.setY(baseY),
    });
  }

  private bouncePortrait(i: number): void {
    const head = this.portraits[i];
    if (!head) return;
    head.setScale(0.6);
    this.tweens.add({ targets: head, scale: head.getData('baseScale') as number, duration: 360, ease: 'Back.Out' });
  }

  private pulseServeHint(): void {
    if (!this.bellBtn) return;
    this.tweens.killTweensOf(this.bellBtn);
    this.bellBtn.setScale(1);
    this.tweens.add({
      targets: this.bellBtn,
      scale: 1.12,
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
  }

  // ── Top UI (sound / home / star / refresh) ───────────────────────────────────
  private buildTopUI(): void {
    // Sound (mute) toggle — just the speaker, no backing square.
    const sound = this.add.image(64, 60, 'sound_icon').setDisplaySize(74, 74).setDepth(30);
    sound.setAlpha(Audio.isMuted ? 0.4 : 1);
    sound.setInteractive({ useHandCursor: true });
    sound.on('pointerdown', () => {
      const muted = Audio.toggleMute();
      sound.setAlpha(muted ? 0.4 : 1);
    });

    // Home (back to menu), then star + refresh, top-right.
    this.makeSquareButton(GAME_W - 248, 60, 'home_icon', 60, () => {
      Audio.unlock();
      this.scene.start('Menu');
    });
    this.makeSquareButton(GAME_W - 156, 60, 'star', 64, () => {
      Audio.unlock();
      this.popStar(GAME_W - 156, 120);
    });
    this.makeSquareButton(GAME_W - 64, 60, 'refresh_icon', 60, () => {
      Audio.unlock();
      if (this.phase === 'build') {
        this.bubble.hide();
        this.startRound();
      }
    });
  }

  /** A white rounded-square button with an icon (top buttons). */
  private makeSquareButton(x: number, y: number, iconKey: string, iconSize: number, onPress: () => void): void {
    const pad = this.add.container(x, y).setDepth(30);
    const bg = this.add.image(0, 0, 'tile').setDisplaySize(84, 84);
    const icon = this.add.image(0, 0, iconKey).setDisplaySize(iconSize, iconSize);
    pad.add([bg, icon]);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', () => {
      this.tweens.add({ targets: pad, scale: 0.9, duration: 80, yoyo: true });
      onPress();
    });
  }

  // ── Waiting customer portraits ───────────────────────────────────────────────
  private buildPortraits(): void {
    // Front (now serving) is bigger; two queued behind it, smaller.
    const slots = [
      { x: 92, y: GAME_H * 0.6, frame: 104, head: 90 },
      { x: 206, y: GAME_H * 0.64, frame: 80, head: 70 },
      { x: 300, y: GAME_H * 0.64, frame: 80, head: 70 },
    ];
    slots.forEach((s) => {
      const c = this.add.container(s.x, s.y).setDepth(7);
      const frame = this.add.image(0, 0, 'tile').setDisplaySize(s.frame, s.frame);
      const head = this.add.image(0, 6, `animal_${SHOPKEEPER}`).setDisplaySize(s.head, s.head * (210 / 200));
      c.add([frame, head]);
      head.setData('baseY', 6);
      head.setData('baseScale', head.scaleX);
      this.portraits.push(head);
    });
  }

  private updatePortraits(): void {
    this.portraits.forEach((head, i) => {
      const idx = (this.customerIdx + i) % CUSTOMER_NAMES.length;
      head.setTexture(`animal_${CUSTOMER_NAMES[idx]}`);
      // Re-fix the display size for the swapped texture (all heads share 200×210).
      const base = head.getData('baseScale') as number;
      head.setScale(base);
    });
  }

  // ── Ingredient shelf + bell ──────────────────────────────────────────────────
  private makeBell(): void {
    this.bellBtn = this.add.container(BELL.x, BELL.y).setDepth(9);
    const bellBg = this.add.image(0, 0, 'tile').setDisplaySize(112, 112);
    const bellIcon = this.add.image(0, 0, 'bell').setDisplaySize(96, 96);
    this.bellBtn.add([bellBg, bellIcon]);
    bellBg.setInteractive({ useHandCursor: true });
    bellBg.on('pointerdown', () => {
      this.tweens.add({ targets: this.bellBtn, scale: 0.9, duration: 80, yoyo: true });
      this.onServe();
    });
  }

  /** Rebuild the ingredient tiles for the current round (flavors vary by difficulty). */
  private rebuildShelf(): void {
    this.shelf.removeAll(true);
    const topRow = ['base_cone', 'base_cup', ...this.shelfFlavors().map((f) => `scoop_${f}`)];

    const layoutRow = (items: string[], y: number) => {
      const spacing = Math.min(152, (GAME_W - 120) / items.length);
      const startX = GAME_W / 2 - ((items.length - 1) * spacing) / 2;
      items.forEach((key, i) => {
        this.shelf.add(this.makeTile(startX + i * spacing, y, key));
      });
    };
    layoutRow(topRow, GAME_H * 0.815);
    layoutRow(TOPPINGS, GAME_H * 0.93);
  }

  private makeTile(x: number, y: number, key: string): Phaser.GameObjects.Container {
    const tile = this.add.container(x, y);
    const bg = this.add.image(0, 0, 'tile').setDisplaySize(112, 112);
    const [w, h] = this.dispFor(key);
    const art = this.add.image(0, 0, key).setDisplaySize(w, h);
    tile.add([bg, art]);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', () => {
      this.tweens.add({ targets: tile, scale: 0.9, duration: 80, yoyo: true });
      this.onTileTap(key);
    });
    return tile;
  }

  /** Display size for an ingredient inside its 112px tile (new art proportions). */
  private dispFor(key: string): [number, number] {
    if (key === 'base_cone') return [56, 70];
    if (key === 'base_cup') return [74, 82];
    if (key.startsWith('scoop_')) return [88, 77];
    if (key === 'top_cherry') return [46, 55];
    if (key === 'top_sprinkles') return [88, 74];
    if (key === 'top_wafer') return [82, 69];
    if (key === 'top_whip') return [60, 75];
    return [80, 80];
  }

  private onTileTap(key: string): void {
    if (key === 'base_cone') this.onPickBase('cone');
    else if (key === 'base_cup') this.onPickBase('cup');
    else if (key.startsWith('scoop_')) this.onPlaceScoop(key.slice('scoop_'.length));
    else if (key.startsWith('top_')) this.onPickTopping(key);
  }

  private showShelf(visible: boolean): void {
    this.shelf.setVisible(visible);
    this.bellBtn.setVisible(visible);
    this.undoZone.setVisible(visible);
    if (!visible) {
      this.tweens.killTweensOf(this.bellBtn);
      this.bellBtn.setScale(1);
    }
  }

  // ── Debug hook for the automated playability check ───────────────────────────
  private exposeDebugApi(): void {
    (window as unknown as { __shop?: unknown }).__shop = {
      state: () => ({
        phase: this.phase,
        difficulty: this.difficulty.id,
        stage: this.stage.id,
        order: this.order,
        built: this.iceCream.scoopCount,
        jar: this.jar.filledCount,
      }),
      place: (flavor: string) => this.onPlaceScoop(flavor),
      base: (b: 'cone' | 'cup') => this.onPickBase(b),
      undo: () => this.onUndo(),
      serve: () => this.onServe(),
      payState: () =>
        this.payment ? { target: this.payment.targetValue, total: this.payment.currentTotal } : null,
      payAdd: (value: number) => this.payment?.addValue(value),
    };
  }
}
